import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { profileError } from '../lib/profileDraft';
import { logoSource } from './VenueLogo';

const TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export default function VenueLogoUpload({ venue, disabled = false, onSaved, onReload, onBusy }) {
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(false);
  const pending = useRef(false), mounted = useRef(false), selection = useRef(0);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; selection.current += 1; }; }, []);
  const choose = async event => {
    const file = event.target.files?.[0]; event.target.value = '';
    if (!file || pending.current || disabled) return;
    setError(''); setMessage('');
    if (!TYPES.includes(file.type) || file.size > 2_000_000) {
      setError('JPEG, PNG veya WebP seç; logo en fazla 2 MB olmalı.'); return;
    }
    const version = ++selection.current;
    try {
      const data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error('Logo dosyası okunamadı.'));
        reader.readAsDataURL(file);
      });
      if (mounted.current && selection.current === version) setDraft(data);
    } catch (e) { if (mounted.current) setError(e.message); }
  };
  const save = async remove => {
    if (pending.current || disabled || !venue || (!remove && !draft)) return;
    pending.current = true; selection.current += 1; setBusy(true); onBusy?.(true);
    setError(''); setMessage(''); setConflict(false);
    try {
      const { data } = await axios.put(`${process.env.REACT_APP_BACKEND_URL || ''}/api/venues/me/logo`, {
        profile_revision: venue.profile_revision ?? 0,
        data: remove ? null : draft.split(',')[1],
      }, { timeout: 20000 });
      if (!data?.id || data.id !== venue.id || !Object.prototype.hasOwnProperty.call(data, 'logo_url')) throw new Error('invalid_response');
      if (!mounted.current) return;
      onSaved(data); setDraft(null);
      setMessage(remove ? 'Logo kaldırıldı.' : 'Logo kaydedildi.');
    } catch (e) {
      if (!mounted.current) return;
      setConflict(e.response?.status === 409 || !e.response);
      setError(!e.response ? 'İşlemin sonucu doğrulanamadı. Logo seçimin korundu; tekrar kaydetmeden önce güncel profili kontrol et.' : profileError(e));
    } finally {
      pending.current = false; onBusy?.(false);
      if (mounted.current) setBusy(false);
    }
  };
  const src = draft || (venue?.logo_url ? logoSource(venue) : '');
  return <section className="mb-6 rounded-2xl border-2 border-zinc-200 bg-white p-5 space-y-3" data-testid="venue-logo-upload">
    <h2 className="text-xl font-bold">İşletme logosu</h2>
    <p className="text-sm text-zinc-600">Profilinde ve uygun mekân kartlarında kullanılır; kapak fotoğrafından ayrıdır. Şeffaf PNG korunur, görsel kesilmez. Logo değişikliği tanıtım bilgileri gibi yeniden onaya gider.</p>
    <div className="flex flex-wrap items-center gap-4">
      {src ? <img src={src} alt="İşletme logosu önizlemesi" width="96" height="96" className="h-24 w-24 object-contain rounded-xl border bg-white p-2" />
        : <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-xl border text-sm text-zinc-500">Logo yok</div>}
      <div className="min-w-0 flex-1 space-y-3">
        <label className="block text-sm font-semibold">Logo seç
          <input className="mt-2 block w-full min-w-0 text-sm" aria-label="İşletme logosu seç" type="file" accept={TYPES.join(',')}
            disabled={disabled || busy || !venue} onChange={choose} />
        </label>
        <div className="flex flex-wrap gap-3">
          {draft && <button type="button" className="action-primary" disabled={disabled || busy} onClick={() => save(false)}>Logoyu kaydet</button>}
          {draft && <button type="button" className="text-action" disabled={disabled || busy} onClick={() => { selection.current += 1; setDraft(null); }}>Vazgeç</button>}
          {venue?.logo_url && <button type="button" className="text-action" disabled={disabled || busy} onClick={() => save(true)}>Logoyu kaldır</button>}
        </div>
      </div>
    </div>
    {!venue && <p className="text-sm">Önce aşağıdaki temel mekân bilgilerini kaydet; ardından logonu buradan ekleyebilirsin.</p>}
    <p className="text-xs text-zinc-500">En fazla 2 MB; JPEG, PNG veya WebP. Yüklenen logo bağlantısı olan kişilerce görülebilir.</p>
    {busy && <p role="status">Logo kaydediliyor…</p>}
    {message && <p role="status">{message}</p>}
    {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    {conflict && <button type="button" className="text-action" disabled={disabled || busy} onClick={onReload}>Güncel profili getir</button>}
  </section>;
}
